<template>
  <main class="ref-page">
    <PageHeader :title="$t('frontend.history.title')" back />
    <section class="ref-section watch-page">
      <div class="watch-intro">
        <img class="watch-intro__avatar" src="/img/mine/avatar.png?v=20260924" alt="">
        <div class="watch-intro__copy">
          <strong>Hi,</strong>
          <p>{{ $t('frontend.history.summary') }}</p>
        </div>
        <div class="watch-intro__balance">
          <span>{{ $t('frontend.player.ticket_balance') }}</span>
          <strong>{{ balance }}</strong>
        </div>
      </div>
      <LoadState v-if="loading && !items.length" loading />
      <van-list class="watch-list" :loading="loading" :finished="finished" :error="Boolean(error)" :offset="200" :finished-text="items.length ? $t('frontend.history.no_more') : ''" @load="load">
        <article v-for="item in items" :key="item.watch_id" class="watch-record">
          <div class="watch-record__row">
            <span class="watch-record__avatar" aria-hidden="true">T</span>
            <div class="watch-record__content">
              <div class="watch-record__heading"><strong>{{ $t('frontend.history.viewing') }}</strong><strong class="watch-record__cost">{{ $t('frontend.history.ticket_cost', { cost: item.watch_cost }) }}</strong></div>
              <time class="watch-record__time">{{ item.watched_at }}</time>
            </div>
          </div>
          <router-link v-if="item.available" class="watch-record__title" :to="{ path: '/PlayVideo', query: { id: item.id } }">{{ item.vod_name }}</router-link>
          <p v-else class="watch-unavailable">{{ item.vod_name || $t('frontend.history.unavailable') }}<span>{{ $t('frontend.history.unavailable') }}</span></p>
        </article>
        <LoadState v-if="!loading && !error && !items.length" :empty="$t('frontend.history.empty')" />
        <template #loading><LoadState v-if="items.length" loading compact /></template>
        <template #error><LoadState :error="error" :compact="Boolean(items.length)" @retry="load" /></template>
      </van-list>
      <section v-if="legacyItems.length" class="watch-legacy">
        <h2>{{ $t('frontend.history.legacy_title') }}</h2>
        <p>{{ $t('frontend.history.legacy_summary') }}</p>
        <div class="ref-video-grid"><VideoCard v-for="item in legacyItems" :key="item.id" :item="item" /></div>
      </section>
    </section>
  </main>
</template>
<script>
import PageHeader from "../../common/PageHeader";
import VideoCard from "../../common/VideoCard";
import LoadState from "../../common/LoadState";
import { assertResponse, displayNumber } from "../../common/reference";
import { readHistory } from "../../common/history";
import { clearSession } from "../../common/language";

export default {
  components: { PageHeader, VideoCard, LoadState },
  data: () => ({ items: [], legacyItems: [], videoScore: undefined, loading: false, error: "", cursor: 0, finished: false, requestVersion: 0 }),
  computed: { balance() { return displayNumber(this.videoScore); } },
  methods: {
    async load() {
      if (this.loading || this.finished) return;
      if (!localStorage.getItem("token")) { this.$router.push("/Login"); return; }
      const version = ++this.requestVersion;
      this.loading = true;
      this.error = "";
      try {
        const response = await this.$http({ method: "get", url: "video_watch_history", data: { cursor: this.cursor, page_size: 20 } });
        if (version !== this.requestVersion) return;
        if (Number(response && response.code) === 401) {
          clearSession();
          this.$store.commit("setUserInfoValue", {});
          this.$router.push("/Login");
          return;
        }
        const result = assertResponse(response);
        if (!Array.isArray(result.list) || result.list.some(item => !item || !item.watch_id)) throw new Error(this.$t('frontend.history.load_failed'));
        const hasMore = result.has_more === true || Number(result.has_more) === 1;
        const cursor = Number(result.next_cursor);
        if (hasMore && (!result.list.length || !Number.isInteger(cursor) || cursor <= 0 || (this.cursor && cursor >= this.cursor))) throw new Error(this.$t('frontend.history.load_failed'));
        this.items = this.items.concat(result.list.filter(item => !this.items.some(old => String(old.watch_id) === String(item.watch_id))));
        this.videoScore = result.video_score;
        this.cursor = result.next_cursor || 0;
        this.finished = !hasMore;
      } catch (error) {
        if (version === this.requestVersion) this.error = error.message || this.$t('frontend.history.load_failed');
      } finally {
        if (version === this.requestVersion) this.loading = false;
      }
    }
  },
  created() {
    if (localStorage.getItem("token")) {
      this.legacyItems = readHistory().filter(item => /^[\x20-\x7E]+$/.test(String(item.vod_name || item.name || item.title || '')));
    }
    this.load();
  },
  beforeDestroy() { this.requestVersion++; }
};
</script>
<style scoped>
.watch-page { padding: 1rem .7rem 1rem; background: var(--ref-bg); }
.watch-intro { position: relative; display: flex; align-items: center; min-height: 6.2rem; padding: .8rem 1rem; overflow: hidden; background: #fff; border-radius: .15rem; box-sizing: border-box; }
.watch-intro__avatar { width: 3.9rem; height: 3.9rem; flex: 0 0 auto; border: .14rem solid #d7b5f7; border-radius: 50%; background: #a04fe0; object-fit: cover; }
.watch-intro__copy { min-width: 0; margin-left: .55rem; padding-right: 6.8rem; color: #4d2b93; }
.watch-intro__copy strong { display: block; font-size: 1.08rem; line-height: 1.2; }
.watch-intro__copy p { margin: .2rem 0 0; color: #6750b4; font-size: 0.7344rem; line-height: 1.35; }
.watch-intro__balance { position: absolute; right: .75rem; top: 50%; display: flex; align-items: center; gap: .12rem; min-height: 1.85rem; padding: 0 .75rem; color: #fff; background: var(--ref-gradient); border-radius: 1.2rem; transform: translateY(-50%); white-space: nowrap; }
.watch-intro__balance span, .watch-intro__balance strong { font-size: 0.7344rem; font-weight: 700; }
.watch-intro__balance strong { font-weight: 700; }
.watch-list { margin-top: .85rem; }
.watch-record { padding: 1rem .55rem .95rem; background: #fff; border-bottom: 1px solid #eee5f0; }
.watch-record__row { display: flex; align-items: flex-start; min-width: 0; }
.watch-record__avatar { display: inline-flex; align-items: center; justify-content: center; width: 3rem; height: 3rem; flex: 0 0 auto; color: #fff; background: #f05a8d; border-radius: 50%; font-size: 1.62rem; }
.watch-record__content { min-width: 0; flex: 1; margin-left: .55rem; }
.watch-record__heading { display: flex; justify-content: space-between; align-items: center; gap: .6rem; min-height: 1.45rem; color: #30207f; font-size: 0.8856rem; }
.watch-record__heading strong:first-child { font-weight: 700; }
.watch-record__cost { flex: 0 0 auto; color: #f04444; font-size: 0.8424rem; font-weight: 700; }
.watch-record__title { display: block; margin: .6rem 0 0; color: #7368b2; font-size: 0.756rem; line-height: 1.5; overflow: hidden; text-decoration: none; text-overflow: ellipsis; white-space: nowrap; }
.watch-record__time { display: block; margin-top: .08rem; color: #30207f; font-size: 0.702rem; line-height: 1.3; }
.watch-unavailable { margin: .6rem 0 0; color: #7368b2; font-size: 0.756rem; line-height: 1.5; overflow-wrap: anywhere; }
.watch-unavailable span { display: block; margin-top: .15rem; color: #84778d; font-size: 0.702rem; }
.watch-legacy { margin-top: 1.25rem; }
.watch-legacy p { color: #84778d; font-size: 0.7776rem; line-height: 1.7; }
.watch-legacy h2 { font-size: 0.972rem; }
@media (max-width: 22rem) {
  .watch-intro { align-items: flex-start; flex-wrap: wrap; }
  .watch-intro__copy { width: calc(100% - 4.45rem); flex: 0 0 calc(100% - 4.45rem); padding-right: 0; }
  .watch-intro__balance { position: static; flex: 0 0 auto; margin: .55rem 0 0 4.45rem; transform: none; }
}
</style>
